<template>
  <section class="bzi-card" aria-label="What stands out" data-test="stands-out-card">
    <header class="bzi-head">
      <span class="bzi-spark" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"></path></svg>
      </span>
      <h3 class="bzi-title">What stands out</h3>
      <span class="bzi-sub">Ask Bizminer read this Industry Financial Profile for you &middot; from your own data, no charge</span>
      <button type="button" class="bzi-hide" @click="$emit('hide')">Hide</button>
    </header>

    <div v-if="phase === 'loading'" class="bzi-status" role="status" data-test="stands-out-loading">
      <svg class="bzi-spin" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
      Reading this Industry Financial Profile&hellip; this can take up to a minute.
    </div>

    <div v-else-if="phase === 'error' && view" class="bzi-error" :class="{ 'is-final': !view.retryable }" role="alert" data-test="stands-out-error">
      <span>{{ view.text }}</span>
      <button v-if="view.retryable" type="button" class="bzi-link" data-test="stands-out-retry" @click="$emit('retry')">Retry</button>
    </div>

    <template v-else-if="phase === 'done' && data">
      <div class="bzi-grid">
        <article v-for="i in data.insights" :key="i.kind" class="bzi-item" :class="'is-' + i.kind" data-test="stands-out-insight">
          <div class="bzi-kind" data-test="stands-out-kind">
            <svg v-if="i.kind === 'trend'" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"></path></svg>
            <svg v-else-if="i.kind === 'peers'" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z"></path></svg>
            <svg v-else width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path></svg>
            {{ kindLabel(i.kind) }}
          </div>
          <h4 class="bzi-item-title">{{ i.title }}</h4>
          <p class="bzi-detail">{{ i.detail }}</p>
          <div class="bzi-foot">
            <span v-if="i.section" class="bzi-section" data-test="stands-out-section">Section: {{ i.section }}</span>
            <button v-if="i.followUp" type="button" class="bzi-ask" data-test="stands-out-ask" @click="$emit('ask', i)">
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"></path></svg>
              Ask about this
            </button>
          </div>
        </article>
      </div>
      <div v-if="!data.complete" class="bzi-partial" data-test="stands-out-partial">
        Showing {{ data.insights.length }} of 3. The rest couldn't be checked against the profile.
      </div>
    </template>
  </section>
</template>

<script>
// "What stands out?" card (CHAT-20). Presentational: the page owns the request
// and passes its state. All text arrives cleaned by chatCore.normalizeInsights
// and is rendered as text only.
const KIND_LABEL = { trend: 'Trend', peers: 'Vs. peers', risk: 'Risk' };

export default {
  name: 'insights-card',
  props: {
    phase: { type: String, required: true }, // loading | done | error
    data: { type: Object, default: null }, // {insights, complete}
    view: { type: Object, default: null }, // {kind, text, retryable} for an error
  },
  methods: {
    kindLabel(k) {
      return KIND_LABEL[k] || k;
    },
  },
};
</script>

<style lang="scss">
$bzi-navy: #003666;
$bzi-cyan: #0cbcf2;

.bzi-card {
  box-sizing: border-box; width: 100%; margin: 0 0 16px; background: #fff; border-top: 3px solid $bzi-navy; border-radius: 0 0 4px 4px;
  box-shadow: 0 1px 4px rgba(0, 30, 60, 0.12); font-family: Roboto, Arial, sans-serif; color: #22303f; text-align: left;
  animation: bzi-in 0.35s ease-out both;
  h3, h4, p { margin: 0; }
}
@keyframes bzi-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.bzi-head { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 10px; padding: 10px 16px; border-bottom: 1px solid #edf0f4; }
.bzi-spark { width: 26px; height: 26px; border-radius: 13px; background: $bzi-navy; color: $bzi-cyan; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.bzi-title { font-family: Inter, Roboto, sans-serif; font-size: 16px; font-weight: 600; color: #13213c; }
.bzi-sub { flex: 1 1 200px; min-width: 0; font-size: 12px; color: #5b6575; }
.bzi-hide { border: 0; background: transparent; padding: 6px 4px; font: inherit; font-size: 13px; color: #3c4656; cursor: pointer; &:hover { color: $bzi-navy; text-decoration: underline; } }

.bzi-status { display: flex; align-items: center; gap: 8px; padding: 16px; font-size: 13px; color: #4f5a6b; }
.bzi-spin { animation: bzi-spin 0.9s linear infinite; transform-origin: 50% 50%; }
@keyframes bzi-spin { to { transform: rotate(360deg); } }
.bzi-error { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin: 12px 16px; padding: 8px 10px; font-size: 13px; line-height: 1.45; color: #a3261c; background: #fdf0ee; border: 1px solid #f1c9c4; border-radius: 8px; }
.bzi-error.is-final { color: #3c4656; background: #f3f5f8; border-color: #dfe5ec; }
.bzi-link { border: 0; background: transparent; padding: 4px 0; font: inherit; font-weight: 700; color: #0a6fb3; text-decoration: underline; cursor: pointer; }

.bzi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); }
.bzi-item { display: flex; flex-direction: column; gap: 6px; padding: 12px 16px 14px; min-width: 0; border-left: 1px solid #edf0f4; &:first-child { border-left: 0; } }
.bzi-kind { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #0a6fb3; }
.bzi-item.is-peers .bzi-kind { color: #3c4656; }
.bzi-item.is-risk .bzi-kind { color: #b25e00; }
.bzi-item-title { font-family: Inter, Roboto, sans-serif; font-size: 14.5px; font-weight: 700; line-height: 1.3; color: #13213c; overflow-wrap: anywhere; }
.bzi-detail { font-size: 13px; line-height: 1.5; color: #3c4656; overflow-wrap: anywhere; }
.bzi-foot { margin-top: auto; padding-top: 6px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.bzi-section { display: inline-block; max-width: 100%; padding: 2px 8px; border-radius: 999px; background: #eaf6fc; color: $bzi-navy; font-size: 11.5px; line-height: 1.5; overflow-wrap: anywhere; }
.bzi-ask {
  display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: 0 12px; border-radius: 17px; border: 1px solid $bzi-navy;
  background: #fff; color: $bzi-navy; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer;
  &:hover { background: $bzi-navy; color: #fff; }
  &:focus-visible { outline: 2px solid $bzi-cyan; outline-offset: 2px; }
}
.bzi-partial { padding: 8px 16px 10px; border-top: 1px solid #edf0f4; font-size: 12px; color: #6a4a00; background: #fffaf0; }

@media (max-width: 900px) {
  .bzi-item { border-left: 0; border-top: 1px solid #edf0f4; &:first-child { border-top: 0; } }
}
@media (max-width: 600px), (pointer: coarse) {
  .bzi-hide { min-height: 44px; min-width: 44px; }
  .bzi-ask { min-height: 44px; }
  .bzi-link { min-height: 44px; }
}
</style>
